import { ChangeEvent, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { ControlledCheckbox } from '@/Atomic/Atoms/Inputs/ControlledCheckbox'
import { Button } from '@/Atomic/Molecules/Button'
import { Textarea } from '@/Atomic/Molecules/Inputs/Textarea'
import { MAX_FILE_SIZE } from '@/Components/Document/const'
import FileSelector from '@/Components/shared/inputs/file-select'
import { errorToast } from '@/Components/Toast'
import { Message } from '@/Data/Message/MessageDataClass'
import { blobToBinary, DataBinaryUpload } from '@/utils/blobToBinary'
import { reportError } from '@/utils/reportError'

interface NewMessageProps {
  subjectId: string
  onSent?: () => void
}

export const NewMessage: React.FC<NewMessageProps> = connect(
  ({ subjectId, onSent }) => {
    const { t } = useTranslation()
    const inputFileRef = useRef<HTMLInputElement>(null)
    const [text, setText] = useState('')
    const [isPublic, setIsPublic] = useState(false)
    const [attachments, setAttachments] = useState<File[] | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [tooLargeError, setTooLargeError] = useState('')
    const [noEmptyTextError, setNoEmptyTextError] = useState('')

    const onSubmit = async () => {
      if (!text) {
        setNoEmptyTextError(t('components.message.errors.noText'))
        return
      }

      setNoEmptyTextError('')
      setIsSubmitting(true)

      try {
        let attachmentsBinary: DataBinaryUpload[] = []
        if (attachments) {
          attachmentsBinary = await Promise.all(
            attachments.map((attachment) => blobToBinary(attachment)),
          )
        }

        await Message.create({
          subjectId,
          text,
          attachments: attachmentsBinary.length ? attachmentsBinary : null,
          public: isPublic,
        })

        setText('')
        setAttachments(null)
        if (inputFileRef.current) {
          inputFileRef.current.value = ''
        }
        onSent?.()
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      } finally {
        setIsSubmitting(false)
      }
    }

    const uploadFile = (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return

      const filesArray = Array.from(e.target.files)
      const isTooLarge = filesArray.some((file) => file.size > MAX_FILE_SIZE)

      if (isTooLarge) {
        setTooLargeError(
          t('components.message.errors.attachmentTooLargeSingle'),
        )
        return
      }

      setTooLargeError('')
      setAttachments(filesArray)
    }

    return (
      <div className="message-tab__composer">
        <div className="message-tab__composer-body">
          <Textarea
            name="new-message"
            label={t('components.message.newMessage')}
            onChange={({ target: { value } }) => setText(value)}
            value={text}
            disabled={isSubmitting}
            error={noEmptyTextError}
          />

          <div className="message-tab__composer-actions">
            <div className="message-tab__composer-actions-left">
              <ControlledCheckbox
                name="newMessageIsPublic"
                label={t('components.message.public')}
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <FileSelector
                name="attachment"
                onChange={uploadFile}
                ref={inputFileRef}
                multiple
                error={tooLargeError}
              />
            </div>
            <Button
              title={t('common.submit')}
              onClick={onSubmit}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>
    )
  },
)
